import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";

import { useLingui, Trans } from "@lingui/react/macro";
import { ApiError, apiFetch } from "@pontimed/api-client";
import { refreshSession } from "@pontimed/auth-client";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AnimatePresence, m } from "framer-motion";
import { X } from "lucide-react";

import {
  CardActionDeclined,
  CardActionOutcomeUnknown,
  CardActionStillProcessing,
  discardPendingPayment,
  PendingChoiceCancelled,
  sendChangeWithCardAction,
} from "@/components/billing/confirm-card-action";
import { usePendingPaymentPrompt } from "@/components/billing/pending-payment-sheet";
import { useServicesPausedBillingMessage } from "@/components/billing/services-paused-billing";
import { hasPerSeatTiers } from "@/components/pricing/clinic-seat-pricing";
import { Button } from "@/components/ui/button";
import { ResponsiveSheet } from "@/components/ui/responsive-sheet";
import { useBrowsingCountry } from "@/lib/hooks/use-browsing-country";
import { isVaultUnlockCancelled, useProtectedAction } from "@/lib/hooks/use-protected-action";
import { buyerPriceFactorOf } from "@/lib/pricing/buyer-price";
import { calculatePlanPrice } from "@/lib/pricing/calculate-plan-price";

import {
  type CheckoutStep,
  type CheckoutModalProps,
  type PlanAddonOption,
  type PricingTier,
  checkoutReducer,
  createInitialState,
  getVisibleSteps,
} from "./checkout-types";
import { CheckoutStepBilling } from "./checkout-step-billing";
import { CheckoutStepCard } from "./checkout-step-card";
import { CheckoutStepConfigure } from "./checkout-step-configure";
import { CheckoutStepPay } from "./checkout-step-pay";
import { CheckoutStepPlan } from "./checkout-step-plan";
import { CheckoutStepSuccess } from "./checkout-step-success";
import { CheckoutStepper } from "./checkout-stepper";
import { CheckoutSummary } from "./checkout-summary";

// Source: legacy src/components/billing/checkout/checkout-modal.tsx (1:1
// port). Differences vs legacy, per SPA conventions:
// - next-intl t("checkout.*") → Lingui macros (legacy en.json values are the
//   msgids).
// - The hand-rolled backdrop + motion.div modal shell → <ResponsiveSheet>
//   (width "4xl", mobileFullScreen). The modal keeps its own per-step
//   hash-history management, so the sheet's built-in history sentinel is
//   turned off via `disableHistory`. Legacy disabled backdrop-close on the
//   "pay" step only — mapped to `dismissible={currentStep !== "pay"}`; the
//   header X lives in our custom `headerSlot` and stays clickable on every
//   step, exactly like legacy.
// - next-auth `update()` + next/navigation `router.refresh()` →
//   `refreshSession()` (re-mints the access cookie with the new
//   subscription claims) + a broad react-query invalidation (closest analog
//   to the RSC refresh).
// - raw fetch() → apiFetch; the plan-addons lookup (legacy useEffect+fetch)
//   → react-query keyed by planCode. The on-open wallet/tiers fetches stay
//   imperative inside the reset effect to preserve the exact
//   once-per-open dispatch semantics.
// - Locale comes from i18n.locale and country from useBrowsingCountry()
//   instead of next/navigation route params.
// - motion.* → m.*.

// ---------------------------------------------------------------------------
// API response shapes (legacy read untyped res.json(); mirror apps/api)
// ---------------------------------------------------------------------------

interface BillingWalletResponse {
  wallet?: { balance?: number } | null;
}

interface PricingTiersResponse {
  tiers?: PricingTier[];
}

// Mirrors POST /api/account/billing/change-plan; shape consumed by the
// CHANGE_RESULT reducer action.
interface ChangePlanResponse {
  success: boolean;
  type: string;
  effectiveDate: string;
  proratedAmount?: number;
}

const PLAN_ADDONS_PATH = "/api/billing/plan-addons";

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function CheckoutModal(props: CheckoutModalProps) {
  const {
    open,
    onClose,
    onSuccess,
    onPaymentComplete,
    subscription,
    planCode,
    entityId,
    workspace,
    onShowServicePause,
  } = props;

  const { t, i18n } = useLingui();
  const queryClient = useQueryClient();
  // Change plan rewrites the subscription and charges the card on file or the
  // wallet: the API answers vault_locked while a PIN guarding "billing" or
  // "wallet" is not unlocked — ask again, retry once.
  const { runProtected, PinGateElement } = useProtectedAction(["billing", "wallet"]);
  const pendingPrompt = usePendingPaymentPrompt();
  const servicesPausedMessage = useServicesPausedBillingMessage();
  const locale = i18n.locale || "ro";
  const { country: browsingCountry } = useBrowsingCountry();
  const country = browsingCountry || "ro";

  // State
  const [state, dispatch] = useReducer(checkoutReducer, props, createInitialState);
  const visibleSteps = useMemo(
    () => getVisibleSteps(props),
    // Stable deps — only recalculate when these specific props change
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [!!subscription, planCode],
  );
  const isChangeMode = !!subscription;

  // Current step
  const [currentStep, setCurrentStepRaw] = useState<CheckoutStep>(visibleSteps[0]);
  const historyDepthRef = useRef(0);
  const suppressPopRef = useRef(false);
  // The billing step's Continue (validation + recording who the invoice goes
  // to). The mobile footer below calls it: a bare goNext skipped both.
  const billingContinueRef = useRef<(() => void) | null>(null);
  // Paid: browser/Android back from the success step must close the modal,
  // not re-mount the pay step (which starts the payment again).
  const paidRef = useRef(false);
  // A step change resolved after the modal closed (the billing step saving a
  // profile, say) must not push a history entry onto the page.
  const openRef = useRef(open);
  useEffect(() => {
    openRef.current = open;
  }, [open]);

  // ── Fetch available add-on packs when plan changes ──────────────────────
  // Source: GET /api/billing/plan-addons → PlanFeature rows with batch config.
  // Cleared on plan change via SELECT_PLAN reducer; re-fetched here (legacy
  // useEffect+fetch → react-query keyed by planCode).
  const addonsQuery = useQuery({
    queryKey: ["api", PLAN_ADDONS_PATH, state.selectedPlanCode],
    queryFn: () =>
      apiFetch.get<{ options?: PlanAddonOption[] }>(
        `${PLAN_ADDONS_PATH}?planCode=${encodeURIComponent(state.selectedPlanCode)}`,
      ),
    enabled: !!state.selectedPlanCode,
    retry: false,
  });

  useEffect(() => {
    if (!state.selectedPlanCode) {
      dispatch({ type: "SET_AVAILABLE_PACKS", packs: [] });
      return;
    }
    if (addonsQuery.isError) {
      dispatch({ type: "SET_AVAILABLE_PACKS", packs: [] });
    } else if (addonsQuery.data) {
      dispatch({ type: "SET_AVAILABLE_PACKS", packs: addonsQuery.data.options ?? [] });
    }
  }, [state.selectedPlanCode, addonsQuery.data, addonsQuery.isError]);

  // The share of the list price the invoice profile pays: net of VAT for a
  // VIES-valid EU company outside BG (lib/pricing/buyer-price.ts), as the
  // server prices the purchase. The profile on screen in the billing step,
  // else the one Continue recorded; a company still being typed is priced
  // once it is saved.
  const priceFactor = useMemo(() => {
    if (state.billingChoice === "individual") return 1;
    const id = state.billingShowNewBusiness
      ? state.selectedProfileId
      : (state.billingSelectedBusinessId ?? state.selectedProfileId);
    return buyerPriceFactorOf(state.billingProfiles.find((p) => p.id === id));
  }, [
    state.billingChoice,
    state.billingShowNewBusiness,
    state.billingSelectedBusinessId,
    state.selectedProfileId,
    state.billingProfiles,
  ]);

  // Price computation — packs resolved from fetched `availablePacks` + user
  // counts in `state.addons.packs`. Legacy `extraBookingsPrice` plumbing is
  // gone; the plan_features DB is now the single source of truth. Configure
  // shows the list (its ladder is the plan's); the summary what this buyer pays.
  const priceWith = useCallback(
    (factor: number) => {
      if (!state.selectedPlanData) return null;
      const packs = state.availablePacks.map((opt) => ({
        featureKey: opt.featureKey,
        batchSize: opt.batchSize,
        batchPriceEur: opt.batchPriceEur,
        count: state.addons.packs[opt.featureKey] ?? 0,
      }));
      return calculatePlanPrice({
        basePriceMonthly: state.selectedPlanData.priceMonthly,
        basePriceAnnual:
          state.selectedPlanData.priceAnnual > 0 ? state.selectedPlanData.priceAnnual : undefined,
        quantity: state.quantity,
        interval: state.interval,
        volumeTiers: state.selectedPlanData.volumeTiers,
        providersIncluded: state.selectedPlanData.providersIncluded ?? 1,
        packs,
        // D2: clinic plans bill ONE flat rung × covered doctors. Only facility
        // plans carry `perSeatTiers` (and `assertPlanFitsEntity` refuses them for
        // any other entity), so this is the same condition apps/api applies.
        seatMode: hasPerSeatTiers(state.selectedPlanData) ? "flat" : "graduated",
        priceFactor: factor,
      });
    },
    [
      state.selectedPlanData,
      state.quantity,
      state.interval,
      state.addons.packs,
      state.availablePacks,
    ],
  );
  const listPrice = useMemo(() => priceWith(1), [priceWith]);
  const price = useMemo(() => priceWith(priceFactor), [priceWith, priceFactor]);

  // ── Navigation ──────────────────────────────────────────────────────────

  const setStep = useCallback(
    (newStep: CheckoutStep) => {
      if (newStep === currentStep || !openRef.current) return;
      const prevIdx = visibleSteps.indexOf(currentStep);
      const newIdx = visibleSteps.indexOf(newStep);
      setCurrentStepRaw(newStep);
      // Forward navigation pushes history (side effect outside updater)
      if (newIdx > prevIdx || newStep === "success" || newStep === "error") {
        window.history.pushState({ checkoutStep: newStep }, "", `#${newStep}`);
        historyDepthRef.current++;
      }
    },
    [currentStep, visibleSteps],
  );

  const goNext = useCallback(() => {
    const idx = visibleSteps.indexOf(currentStep);
    if (idx < visibleSteps.length - 1) {
      setStep(visibleSteps[idx + 1]);
    }
  }, [currentStep, visibleSteps, setStep]);

  const goToStep = useCallback(
    (targetStep: CheckoutStep) => {
      const targetIdx = visibleSteps.indexOf(targetStep);
      const currentIdx = visibleSteps.indexOf(currentStep);
      if (targetIdx < currentIdx) {
        // Going back: directly set step + replace history state
        setCurrentStepRaw(targetStep);
        window.history.replaceState({ checkoutStep: targetStep }, "", `#${targetStep}`);
        // Adjust depth counter
        historyDepthRef.current = Math.max(0, historyDepthRef.current - (currentIdx - targetIdx));
      }
    },
    [currentStep, visibleSteps],
  );

  // ── History management ──────────────────────────────────────────────────

  const cleanupHistory = useCallback(() => {
    suppressPopRef.current = true;
    const depth = historyDepthRef.current;
    historyDepthRef.current = 0;
    if (depth > 0) {
      window.history.go(-depth);
    }
  }, []);

  const handleClose = useCallback(() => {
    cleanupHistory();
    // Remove hash from URL so modal doesn't reopen
    if (window.location.hash) {
      window.history.replaceState(null, "", window.location.pathname + window.location.search);
    }
    onClose();
  }, [cleanupHistory, onClose]);

  const goPrev = useCallback(() => {
    const idx = visibleSteps.indexOf(currentStep);
    if (idx > 0) {
      const prevStep = visibleSteps[idx - 1];
      setCurrentStepRaw(prevStep);
      window.history.replaceState({ checkoutStep: prevStep }, "", `#${prevStep}`);
      historyDepthRef.current = Math.max(0, historyDepthRef.current - 1);
    } else {
      handleClose();
    }
  }, [currentStep, visibleSteps, handleClose]);

  // Popstate listener
  useEffect(() => {
    if (!open) return;
    const onPopState = (e: PopStateEvent) => {
      if (suppressPopRef.current) {
        suppressPopRef.current = false;
        return;
      }
      if (historyDepthRef.current > 0) {
        historyDepthRef.current--;
      }
      const prevStep = (e.state as { checkoutStep?: CheckoutStep } | null)?.checkoutStep;
      if (paidRef.current) {
        handleClose();
      } else if (prevStep && visibleSteps.includes(prevStep)) {
        setCurrentStepRaw(prevStep);
      } else {
        onClose();
      }
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [open, onClose, handleClose, visibleSteps]);

  // ── Reset on open ───────────────────────────────────────────────────────

  useEffect(() => {
    if (open) {
      dispatch({
        type: "RESET",
        initial: {
          selectedPlanCode: props.planCode || "",
          selectedPlanData: props.planData,
          selectedPlanName: props.planData?.name || props.planCode || "",
          entityType: props.entityType,
          interval: props.initialInterval || "monthly",
          quantity: props.initialQuantity || 1,
        },
      });
      const firstStep = visibleSteps[0];
      setCurrentStepRaw(firstStep);
      paidRef.current = false;
      // The last close set it for its own history.go(-depth), whose popstate
      // arrived after the listener was gone: left set, it would swallow the
      // first back of this opening.
      suppressPopRef.current = false;
      historyDepthRef.current = 0;
      window.history.pushState({ checkoutStep: firstStep }, "", `#${firstStep}`);
      historyDepthRef.current = 1;

      // Fetch wallet balance
      apiFetch
        .get<BillingWalletResponse>("/api/account/billing")
        .then((d) => dispatch({ type: "SET_WALLET_BALANCE", balance: d?.wallet?.balance || 0 }))
        .catch(() => {});

      // If planData wasn't provided, fetch it from pricing tiers API
      const needsPlanCode = props.planCode || props.subscription?.planTier;
      if (needsPlanCode && !props.planData) {
        const code = needsPlanCode;
        const targetType = props.entityType || props.subscription?.entityType || "provider";
        apiFetch
          .get<PricingTiersResponse>(
            `/api/pricing/tiers?targetType=${targetType}&country=${country}&locale=${locale}`,
          )
          .then((d) => {
            const tiers = d.tiers || [];
            const match = tiers.find((tier) => tier.planCode === code);
            if (match) {
              dispatch({
                type: "SELECT_PLAN",
                planCode: match.planCode || code,
                planName: match.name || code,
                planData: match,
                entityType: (props.entityType || props.subscription?.entityType || "provider") as
                  | "provider"
                  | "facility"
                  | "business"
                  | "user",
              });
              // Also set interval/quantity from existing subscription
              if (props.subscription) {
                dispatch({
                  type: "SET_INTERVAL",
                  interval: props.subscription.planInterval === "annual" ? "annual" : "monthly",
                });
                if (props.subscription.quantity && props.subscription.quantity > 1) {
                  dispatch({ type: "SET_QUANTITY", quantity: props.subscription.quantity });
                }
              }
            }
          })
          .catch(() => {});
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    open,
    props.planCode,
    props.planData,
    props.entityType,
    props.initialInterval,
    props.initialQuantity,
  ]);

  // ── Payment complete handler ────────────────────────────────────────────

  const handlePaymentComplete = useCallback(async () => {
    // First, before the await: a back gesture during refreshSession must
    // already close the modal instead of re-opening the payment.
    paidRef.current = true;
    // Legacy: await updateSession(); router.refresh()
    await refreshSession().catch(() => false);
    void queryClient.invalidateQueries();
    // No client-side "auto-allocate to self" any more: the server attributes
    // the row to the caller's own provider at checkout, so the plan applies
    // the moment payment completes (the old POST created a ghost seat with
    // providerId=null when the payer had no provider profile yet).
    setStep("success");
    onPaymentComplete?.();
  }, [queryClient, setStep, onPaymentComplete]);

  // ── Change-plan handler (for subscription mode) ─────────────────────────

  const handleChangePlan = useCallback(async () => {
    if (!subscription) return;
    dispatch({ type: "SET_LOADING", loading: true });
    dispatch({ type: "SET_ERROR", error: null });
    const send = () =>
      runProtected(() =>
        apiFetch.post<ChangePlanResponse>("/api/account/billing/change-plan", {
          subscriptionId: subscription.id,
          newPlanCode: state.selectedPlanCode,
        }),
      );
    try {
      // An upgrade is billed at once: when the bank asks for 3-D Secure the
      // customer confirms it, then the same change is sent again — applied on
      // Stripe by then, it is recorded without a second charge. A declined
      // confirmation discards the held change; an earlier change still held
      // for its confirmation is settled first.
      let data: ChangePlanResponse;
      try {
        data = await sendChangeWithCardAction(
          send,
          (secret) => discardPendingPayment(subscription.id, runProtected, secret),
          pendingPrompt.ask,
        );
      } catch (err) {
        // The earlier held payment left as it is: nothing was sent.
        if (err instanceof PendingChoiceCancelled) return;
        if (!(err instanceof CardActionDeclined)) throw err;
        dispatch({
          type: "SET_ERROR",
          error: t`The payment was not confirmed, so nothing was changed.`,
        });
        return;
      }
      dispatch({ type: "CHANGE_RESULT", result: data });
      setTimeout(() => {
        onSuccess?.();
        handleClose();
      }, 2500);
    } catch (err) {
      // The PIN gate was dismissed: nothing changed, the step stays as it was.
      if (isVaultUnlockCancelled(err)) return;
      if (err instanceof CardActionStillProcessing) {
        // The payment went through after all: the page shows the change soon.
        onSuccess?.();
        dispatch({
          type: "SET_ERROR",
          error: t`Your payment went through and is still being applied. Refresh in a moment to see the change.`,
        });
        return;
      }
      if (err instanceof CardActionOutcomeUnknown) {
        onSuccess?.();
        dispatch({
          type: "SET_ERROR",
          error: t`We could not confirm what happened to this payment. Refresh to see the latest state.`,
        });
        return;
      }
      if (err instanceof ApiError) {
        const body = err.body as { error?: string; code?: string } | null;
        // The server's sentences are English-only developer copy; translate the
        // documented codes (D2 added the three clinic-plan ones).
        const byCode =
          body?.code === "plan_misconfigured"
            ? t`This plan is not set up for per-doctor billing yet. Please contact support.`
            : body?.code === "plan_not_purchasable"
              ? t`This plan cannot be bought right now.`
              : body?.code === "annual_not_available_for_coverage"
                ? t`Clinic coverage is billed monthly — annual is not available.`
                : body?.code === "subscription_cancelled"
                  ? t`This subscription is cancelled — reactivate it first.`
                  : body?.code === "subscription_busy"
                    ? t`Another change to this subscription is in progress. Please try again in a moment.`
                    : body?.code === "subscription_changed"
                      ? t`This subscription changed in the meantime. Reload and try again.`
                      : body?.code === "subscription_lease_unavailable"
                        ? t`Billing is temporarily unavailable. Please try again in a moment.`
                        : body?.code === "founding_upgrade_unavailable"
                          ? t`This change is not available on your Founding plan. Please contact support.`
                          : body?.code === "payment_failed"
                            ? t`The payment for this change did not go through, so nothing was changed.`
                            : body?.code === "payment_not_returned"
                              ? t`This subscription changed in the meantime and the payment could not be returned automatically. Our team will refund it — please do not try again.`
                              : body?.code === "payment_processing"
                                ? t`A payment for this subscription is still being processed. Please try again in a moment.`
                                : body?.code === "SERVICES_PAUSED"
                                  ? servicesPausedMessage
                                  : null;
        dispatch({
          type: "SET_ERROR",
          error: byCode || body?.error || t`Something went wrong. Please try again.`,
        });
      } else {
        dispatch({ type: "SET_ERROR", error: t`Something went wrong. Please try again.` });
      }
    } finally {
      dispatch({ type: "SET_LOADING", loading: false });
    }
  }, [
    subscription,
    state.selectedPlanCode,
    t,
    onSuccess,
    handleClose,
    runProtected,
    pendingPrompt.ask,
    servicesPausedMessage,
  ]);

  // ── Render ──────────────────────────────────────────────────────────────

  const showStepper = !isChangeMode && currentStep !== "success" && currentStep !== "error";

  const headerTitle =
    currentStep === "success"
      ? t`Thank you!`
      : isChangeMode
        ? t`Change plan`
        : t`Choose subscription`;

  return (
    <ResponsiveSheet
      open={open}
      onClose={handleClose}
      width="4xl"
      mobileFullScreen
      // The modal manages its own per-step hash history (pushState on
      // forward, popstate = step back); the sheet's sentinel would conflict.
      disableHistory
      // Legacy disabled backdrop-close while paying; the X below stays
      // clickable on every step exactly like legacy.
      dismissible={currentStep !== "pay"}
      ariaLabel={headerTitle}
      headerSlot={
        <div className="flex shrink-0 items-center justify-between border-b px-5 py-3">
          <div className="flex items-center gap-2">
            {currentStep !== "success" && currentStep !== "error" && (
              <button
                onClick={goPrev}
                // Not while a step is saving: its Continue would land after.
                disabled={state.loading}
                aria-label={t`Back`}
                className="text-muted-foreground hover:text-foreground -ml-1 rounded-lg p-1 transition-colors disabled:opacity-40"
              >
                <svg
                  className="h-5 w-5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                </svg>
              </button>
            )}
            <h2 className="text-foreground text-base font-bold">{headerTitle}</h2>
          </div>
          <button
            onClick={handleClose}
            aria-label={t`Close`}
            className="text-muted-foreground hover:text-foreground rounded-lg p-1.5 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      }
      bodyClassName="flex flex-col overflow-hidden p-0 sm:p-0"
    >
      {/* Stepper */}
      {showStepper && (
        <CheckoutStepper
          steps={visibleSteps.filter((s) => s !== "success" && s !== "error") as CheckoutStep[]}
          currentStep={currentStep}
          onStepClick={goToStep}
        />
      )}

      {/* Content + Summary */}
      <div className="flex min-h-0 flex-1 overflow-hidden">
        {/* Main content area */}
        <div className="min-w-0 flex-1 overflow-y-auto">
          <AnimatePresence mode="wait">
            {currentStep === "plan" && (
              <m.div
                key="plan"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <CheckoutStepPlan
                  defaultCategory={props.defaultCategory}
                  onSelectPlan={(planCode, planName, entityType, planData, interval) => {
                    dispatch({
                      type: "SELECT_PLAN",
                      planCode,
                      planData,
                      planName,
                      entityType: entityType as "provider" | "facility" | "business" | "user",
                    });
                    dispatch({ type: "SET_INTERVAL", interval });
                    goNext();
                  }}
                />
              </m.div>
            )}

            {currentStep === "configure" && state.selectedPlanData && (
              <m.div
                key="configure"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
              >
                <CheckoutStepConfigure
                  state={state}
                  price={listPrice}
                  dispatch={dispatch}
                  planData={state.selectedPlanData}
                  onContinue={isChangeMode ? handleChangePlan : goNext}
                  onBack={visibleSteps.indexOf("configure") > 0 ? goPrev : undefined}
                />
              </m.div>
            )}

            {currentStep === "billing" && (
              <m.div
                key="billing"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
              >
                <CheckoutStepBilling
                  state={state}
                  dispatch={dispatch}
                  onContinue={goNext}
                  onBack={goPrev}
                  continueRef={billingContinueRef}
                />
              </m.div>
            )}

            {currentStep === "card" && (
              <m.div
                key="card"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
              >
                <CheckoutStepCard
                  state={state}
                  dispatch={dispatch}
                  onContinue={goNext}
                  onBack={goPrev}
                />
              </m.div>
            )}

            {currentStep === "pay" && (
              <m.div
                key="pay"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
              >
                <CheckoutStepPay
                  state={state}
                  dispatch={dispatch}
                  onComplete={handlePaymentComplete}
                  planCode={state.selectedPlanCode}
                  interval={state.interval}
                  entityType={state.entityType}
                  // The category picked INSIDE the modal decides: a user_plus
                  // bought from a business workspace must not carry the
                  // business id, and a business plan must.
                  entityId={
                    workspace?.entityId && state.entityType === workspace.entityType
                      ? workspace.entityId
                      : entityId
                  }
                  quantity={state.quantity}
                  addons={state.addons}
                  selectedProfileId={state.selectedProfileId}
                  locale={locale}
                  countryCode={country.toUpperCase()}
                  onShowServicePause={onShowServicePause}
                />
              </m.div>
            )}

            {currentStep === "success" && (
              <m.div
                key="success"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
              >
                <CheckoutStepSuccess
                  state={state}
                  entityType={state.entityType}
                  locale={locale}
                  country={country}
                  onClose={() => {
                    onSuccess?.();
                    handleClose();
                  }}
                />
              </m.div>
            )}

            {currentStep === "error" && (
              <m.div
                key="error"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="flex flex-col items-center gap-4 py-16"
              >
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-red-100 dark:bg-red-950">
                  <X className="h-7 w-7 text-red-600" />
                </div>
                <p className="text-foreground text-center text-sm font-medium">
                  {state.error || t`Something went wrong. Please try again.`}
                </p>
                <button
                  onClick={() => {
                    dispatch({ type: "SET_ERROR", error: null });
                    setCurrentStepRaw(visibleSteps[0]);
                  }}
                  className="text-primary text-sm underline"
                >
                  <Trans>Try again</Trans>
                </button>
              </m.div>
            )}
          </AnimatePresence>
        </div>

        {/* Desktop summary sidebar */}
        {currentStep !== "plan" && currentStep !== "success" && currentStep !== "error" && (
          <CheckoutSummary
            state={state}
            price={price}
            currentStep={currentStep}
            variant="desktop"
          />
        )}
      </div>

      {/* Mobile footer: summary strip + navigation buttons. Bottom safe-area
          is owned by the ResponsiveSheet panel (mobileFullScreen, no sheet
          footer), so unlike legacy no pb-[env(...)] here. */}
      {currentStep !== "plan" &&
        currentStep !== "success" &&
        currentStep !== "error" &&
        currentStep !== "pay" && (
          <div className="border-t md:hidden">
            {/* Expandable summary strip */}
            <CheckoutSummary
              state={state}
              price={price}
              currentStep={currentStep}
              variant="mobile"
            />
            {/* Navigation buttons */}
            <div className="flex gap-3 px-5 py-3">
              {visibleSteps.indexOf(currentStep) > 0 && (
                <Button
                  variant="outline"
                  size="lg"
                  onClick={goPrev}
                  disabled={state.loading}
                  className="shrink-0"
                >
                  <Trans>Back</Trans>
                </Button>
              )}
              <Button
                size="lg"
                className="flex-1"
                disabled={state.loading}
                onClick={
                  currentStep === "configure"
                    ? isChangeMode
                      ? handleChangePlan
                      : goNext
                    : currentStep === "billing"
                      ? () => billingContinueRef.current?.()
                      : goNext
                }
              >
                {state.loading ? t`Preparing checkout...` : t`Continue`}
              </Button>
            </div>
          </div>
        )}
      {PinGateElement}
      {pendingPrompt.sheet}
    </ResponsiveSheet>
  );
}
